import { h, compile, Fragment } from 'vue';
import * as Vue from 'vue';
import { renderTemplateWithStableSections, autoMarkStableSections, clearStableSectionCache } from './StableRenderer.js';

/**
 * Create a robust compile function that works in both dev and production
 */
export const getCompileFunction = () => {
  // First try the direct import from vue
  if (compile && typeof compile === 'function') {
    console.log('✅ Using compile from direct import');
    return compile;
  }
  
  // Try to get compile from the Vue runtime (full build)
  if (Vue.compile && typeof Vue.compile === 'function') {
    console.log('✅ Using Vue.compile from runtime');
    return Vue.compile;
  }
  
  // Try to get compile from the global Vue object (if available)
  if (typeof window !== 'undefined' && window.Vue && window.Vue.compile) {
    console.log('✅ Using window.Vue.compile');
    return window.Vue.compile;
  }
  
  // Last resort: try to import compile dynamically
  try {
    // This will only work if the runtime compiler is available
    const { compile: vueCompile } = Vue;
    if (vueCompile && typeof vueCompile === 'function') {
      console.log('✅ Using destructured Vue compile');
      return vueCompile;
    }
  } catch (e) {
    console.warn('Could not access Vue compile function:', e);
  }
  
  console.warn('❌ No Vue compile function found, will use manual processing fallback');
  return null;
};

/**
 * Test if the compile function is working correctly
 */
export const testCompileFunction = () => {
  console.log('Testing Vue compile function...');
  
  const compileFunction = getCompileFunction();
  
  if (!compileFunction || typeof compileFunction !== 'function') {
    console.error('❌ Compile function is not available!');
    return false;
  }
  
  try {
    const testTemplate = '<div>{{ message }}</div>';
    const compiledRender = compileFunction(testTemplate);
    
    if (typeof compiledRender === 'function') {
      console.log('✅ Vue compile function is working correctly!');
      console.log('Compiled render function:', compiledRender.toString().substring(0, 100) + '...');
      return true;
    } else {
      console.error('❌ Compile function did not return a render function');
      return false;
    }
  } catch (error) {
    console.error('❌ Error testing compile function:', error);
    return false;
  }
};

// Cache for stable elements to prevent unnecessary re-creation
const elementCache = new Map();
const imageCache = new Map();
const vnodeCache = new Map();
const stableKeys = new Map(); // Global store for stable keys
const fragmentCache = new Map(); // Cache for stable template fragments

/**
 * Clear all caches - useful for development or when you need to force refresh
 */
export const clearTemplateCache = () => {
  elementCache.clear();
  imageCache.clear();
  vnodeCache.clear();
  stableKeys.clear();
  fragmentCache.clear();
  clearStableSectionCache();
  console.log('Template caches cleared');
};

/**
 * Generate a cache key for an element
 */
const generateCacheKey = (tagName, attrs, content) => {
  const attrString = Object.entries(attrs || {})
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, value]) => `${key}="${value}"`)
    .join(' ');
  return `${tagName}:${attrString}:${content}`;
};

/**
 * Generate a stable hash for HTML content that doesn't change when variables change
 */
const generateStableHash = (html) => {
  // Remove dynamic parts and normalize the HTML structure
  let normalized = html
    .replace(/\{\{\s*[^}]+\s*\}\}/g, '{{VAR}}') // Replace all variable expressions with placeholder
    .replace(/\s+/g, ' ') // Normalize whitespace
    .replace(/>\s+</g, '><') // Remove whitespace between tags
    .trim();
  
  // Simple hash function
  let hash = 0;
  for (let i = 0; i < normalized.length; i++) {
    const char = normalized.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return hash.toString();
};

/**
 * Create a VNode from HTML using Virtual DOM instead of innerHTML
 */
export const createVNodeFromHtml = (html, context, componentInstance) => {
  if (!html) return null;
  
  // Parse HTML into DOM for processing
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
  const container = doc.body.firstChild;
  
  // Convert DOM to VNodes recursively with fragment caching
  return convertDomToVNodeWithCaching(container, context, componentInstance);
};

/**
 * Convert DOM element to VNode with intelligent fragment caching
 */
const convertDomToVNodeWithCaching = (domElement, context, componentInstance, depth = 0) => {
  if (!domElement) return null;
  
  // For top-level or important elements, check if we can cache the fragment
  if (depth <= 2) { // Only cache at shallow levels to avoid over-caching
    const fragmentKey = generateFragmentKey(domElement, context);
    if (fragmentKey && fragmentCache.has(fragmentKey)) {
      const cached = fragmentCache.get(fragmentKey);
      if (isFragmentStillValid(cached, context)) {
        console.log('🔄 Reusing cached fragment:', fragmentKey.substring(0, 50) + '...');
        return cached.vnode;
      } else {
        // Remove invalid cache entry
        fragmentCache.delete(fragmentKey);
      }
    }
  }
  
  // Convert normally if not cached
  const vnode = convertDomToVNode(domElement, context, componentInstance, depth);
  
  // Cache the fragment if it's stable
  if (depth <= 2 && vnode && isFragmentCacheable(domElement)) {
    const fragmentKey = generateFragmentKey(domElement, context);
    if (fragmentKey) {
      fragmentCache.set(fragmentKey, {
        vnode,
        context: { ...context },
        timestamp: Date.now()
      });
      console.log('💾 Cached new fragment:', fragmentKey.substring(0, 50) + '...');
    }
  }
  
  return vnode;
};

/**
 * Generate a key for a DOM fragment that identifies its structure without variable content
 */
const generateFragmentKey = (element, context) => {
  if (element.nodeType !== Node.ELEMENT_NODE) return null;
  
  // Create a signature of the element structure without dynamic content
  const tagName = element.tagName.toLowerCase();
  const classNames = element.className || '';
  const id = element.id || '';
  
  // Get a structural signature without template variables
  let innerHTML = element.innerHTML;
  // Remove template expressions to get stable structure
  innerHTML = innerHTML.replace(/\{\{\s*[^}]+\s*\}\}/g, '{{VAR}}');
  // Remove whitespace variations
  innerHTML = innerHTML.replace(/\s+/g, ' ').trim();
  
  // Don't cache if there are dynamic parts we can't handle
  if (innerHTML.includes('v-if') || innerHTML.includes('v-for') || innerHTML.includes('v-show')) {
    return null;
  }
  
  return `fragment:${tagName}:${id}:${classNames}:${innerHTML.substring(0, 200)}`;
};

/**
 * Check if a fragment is still valid for the current context
 */
const isFragmentStillValid = (cached, currentContext) => {
  // Check if the fragment is too old (invalidate after 30 seconds to prevent memory leaks)
  if (Date.now() - cached.timestamp > 30000) {
    return false;
  }
  
  // For now, we'll be conservative and only reuse if context hasn't changed much
  // In a real implementation, you might want more sophisticated validation
  const oldKeys = Object.keys(cached.context);
  const newKeys = Object.keys(currentContext);
  
  // If context structure changed significantly, invalidate
  if (Math.abs(oldKeys.length - newKeys.length) > 2) {
    return false;
  }
  
  return true;
};

/**
 * Check if a fragment is suitable for caching
 */
const isFragmentCacheable = (element) => {
  if (element.nodeType !== Node.ELEMENT_NODE) return false;
  
  // Cache elements that contain images or other media
  const hasImages = element.querySelector('img');
  const hasVideo = element.querySelector('video');
  const hasIframe = element.querySelector('iframe');
  
  // Cache containers with stable IDs or classes
  const hasStableId = element.id && !element.id.includes('{{');
  const hasStableClass = element.className && !element.className.includes('{{');
  
  return hasImages || hasVideo || hasIframe || hasStableId || hasStableClass;
};

/**
 * Convert DOM element to VNode recursively
 */
const convertDomToVNode = (domElement, context, componentInstance, depth = 0) => {
  if (!domElement) return null;
  
  // Prevent infinite recursion
  if (depth > 50) {
    console.warn('Maximum recursion depth reached in convertDomToVNode');
    return null;
  }
  
  if (domElement.nodeType === Node.TEXT_NODE) {
    const text = processTextExpressions(domElement.textContent, context);
    return text;
  }
  
  if (domElement.nodeType !== Node.ELEMENT_NODE) {
    return null;
  }
  
  const tagName = domElement.tagName.toLowerCase();
  const attrs = {};
  const children = [];
  
  // Process attributes
  Array.from(domElement.attributes).forEach(attr => {
    let name = attr.name;
    let value = attr.value;
    
    // Handle Vue directives
    if (name.startsWith('v-')) {
      const result = processDirective(name, value, context);
      if (result) {
        Object.assign(attrs, result);
      }
      return; // Don't add the directive as a regular attribute
    }
    
    // Handle event listeners
    if (name.startsWith('@')) {
      const eventName = name.substring(1);
      attrs[`on${eventName.charAt(0).toUpperCase()}${eventName.slice(1)}`] = (event) => {
        handleDynamicEvent(value, context, componentInstance, event);
      };
      return;
    }
    
    // Process attribute value for expressions
    value = processTextExpressions(value, context);
    
    // Convert HTML attributes to Vue props
    if (name === 'class') {
      attrs.class = value;
    } else if (name === 'style') {
      attrs.style = value;
    } else {
      attrs[name] = value;
    }
  });
  
  // Process children
  Array.from(domElement.childNodes).forEach(child => {
    const childVNode = convertDomToVNode(child, context, componentInstance, depth + 1);
    if (childVNode !== null) {
      children.push(childVNode);
    }
  });
  
  // Special handling for images to prevent reloading
  if (tagName === 'img' && attrs.src) {
    // Create a stable identifier for this image
    const imageIdentifier = `${attrs.src}|${attrs.alt || ''}`;
    
    let imageKey;
    if (stableKeys.has(imageIdentifier)) {
      // Use existing stable key
      imageKey = stableKeys.get(imageIdentifier);
      console.log('🔄 Reusing stable key for image:', imageKey, attrs.src);
    } else {
      // Create new stable key and store it
      imageKey = `img-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      stableKeys.set(imageIdentifier, imageKey);
      console.log('🆕 Created new stable key for image:', imageKey, attrs.src);
    }
    
    // Always create a new VNode but with a stable key
    // Vue's diffing algorithm will preserve the DOM element if the key is the same
    const vnode = h(tagName, { 
      ...attrs, 
      key: imageKey,
      // Add a data attribute to help with debugging
      'data-stable-key': imageKey
    }, children);
    
    return vnode;
  }
  
  // For nested anchor tags, ensure proper nesting by converting inner anchors to spans
  if (tagName === 'a') {
    const processedChildren = children.map(child => {
      if (typeof child === 'object' && child.type === 'a') {
        // Convert nested anchor to span to avoid HTML validation issues
        const childClass = child.props?.class || '';
        const childStyle = child.props?.style || '';
        return h('span', {
          ...child.props,
          class: childClass + (childClass ? ' ' : '') + 'nested-link',
          style: childStyle + (childStyle ? '; ' : '') + 'text-decoration: underline; cursor: pointer;',
          onClick: child.props?.onClick || (() => {
            if (child.props?.href) {
              window.open(child.props.href, child.props?.target || '_blank');
            }
          })
        }, child.children);
      }
      return child;
    });
    
    return h(tagName, attrs, processedChildren);
  }
  
  // For all other elements, add a stable key if they have stable content
  // This helps Vue's diffing algorithm preserve DOM elements across re-renders
  if (tagName && attrs.src) {
    // Elements with src attributes (like video, audio, iframe) should have stable keys
    const stableKey = `${tagName}-${btoa(attrs.src).replace(/[^a-zA-Z0-9]/g, '')}`;
    attrs.key = stableKey;
    attrs['data-stable-key'] = stableKey;
  } else if (tagName && (attrs.id || attrs.class)) {
    // Elements with stable identifiers can have stable keys
    const identifier = attrs.id || attrs.class;
    if (identifier && !identifier.includes('{{')) { // Don't cache if it contains template variables
      const stableKey = `${tagName}-${identifier.replace(/[^a-zA-Z0-9-_]/g, '')}`;
      attrs.key = stableKey;
    }
  }
  
  return h(tagName, attrs, children);
};

/**
 * Process Vue directive and return appropriate props
 */
const processDirective = (name, value, context) => {
  switch (name) {
    case 'v-if':
      const condition = evaluateExpression(value, context);
      if (!condition) {
        return { style: 'display: none;', 'data-v-if-hidden': 'true' };
      }
      break;
      
    case 'v-show':
      const showCondition = evaluateExpression(value, context);
      if (!showCondition) {
        return { style: 'display: none;' };
      }
      break;
      
    case 'v-text':
      const textResult = evaluateExpression(value, context);
      return { textContent: textResult !== undefined ? String(textResult) : '' };
      
    case 'v-html':
      const htmlResult = evaluateExpression(value, context);
      return { innerHTML: htmlResult !== undefined ? String(htmlResult) : '' };
  }
  
  return null;
};

/**
 * Handle dynamic event with proper context
 */
const handleDynamicEvent = (expression, context, componentInstance, event) => {
  try {
    // Create a function that evaluates the expression with proper context
    const func = new Function('context', 'event', `
      with (context) {
        return ${expression};
      }
    `);
    
    const result = func(context, event);
    
    // If the result is a promise, handle it
    if (result && typeof result.then === 'function') {
      result.catch(error => {
        console.error('Error in async event handler:', error);
      });
    }
    
    return result;
  } catch (error) {
    console.error('Error in event handler:', expression, error);
  }
};

/**
 * Process HTML for reactivity by evaluating Vue expressions and directives
 */
export const processHtmlForReactivity = (html, context) => {
  if (!html) return '';
  
  console.log('Processing HTML for reactivity:', html);
  
  // Parse HTML into a DOM structure for processing
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
  const container = doc.body.firstChild;
  
  // Process the DOM tree
  processElement(container, context);
  
  // Convert back to HTML
  const processedHtml = container.innerHTML;
  
  console.log('Processed HTML result:', processedHtml);
  return processedHtml;
};

/**
 * Process a DOM element for Vue directives and expressions
 */
const processElement = (element, context) => {
  if (!element || element.nodeType !== Node.ELEMENT_NODE) return;
  
  // Handle v-if directive
  if (element.hasAttribute('v-if')) {
    const condition = element.getAttribute('v-if');
    const result = evaluateExpression(condition, context);
    if (!result) {
      element.style.display = 'none';
      element.setAttribute('data-v-if-hidden', 'true');
    } else {
      element.style.display = '';
      element.removeAttribute('data-v-if-hidden');
    }
    element.removeAttribute('v-if');
  }
  
  // Handle v-show directive
  if (element.hasAttribute('v-show')) {
    const condition = element.getAttribute('v-show');
    const result = evaluateExpression(condition, context);
    element.style.display = result ? '' : 'none';
    element.removeAttribute('v-show');
  }
  
  // Handle v-text directive
  if (element.hasAttribute('v-text')) {
    const expression = element.getAttribute('v-text');
    const result = evaluateExpression(expression, context);
    element.textContent = result !== undefined ? String(result) : '';
    element.removeAttribute('v-text');
  }
  
  // Handle v-html directive
  if (element.hasAttribute('v-html')) {
    const expression = element.getAttribute('v-html');
    const result = evaluateExpression(expression, context);
    element.innerHTML = result !== undefined ? String(result) : '';
    element.removeAttribute('v-html');
    return; // Don't process children if we're setting innerHTML
  }
  
  // Convert @click to data-click
  if (element.hasAttribute('@click')) {
    const clickHandler = element.getAttribute('@click');
    element.setAttribute('data-click', clickHandler);
    element.removeAttribute('@click');
  }
  
  // Process text nodes for {{ }} expressions
  processTextNodes(element, context);
  
  // Process child elements recursively
  Array.from(element.children).forEach(child => {
    processElement(child, context);
  });
};

/**
 * Process text nodes for Vue expressions {{ }}
 */
const processTextNodes = (element, context) => {
  const walker = document.createTreeWalker(
    element,
    NodeFilter.SHOW_TEXT,
    null,
    false
  );
  
  const textNodes = [];
  let node;
  while (node = walker.nextNode()) {
    textNodes.push(node);
  }
  
  textNodes.forEach(textNode => {
    const originalText = textNode.textContent;
    const processedText = processTextExpressions(originalText, context);
    if (processedText !== originalText) {
      textNode.textContent = processedText;
    }
  });
};

/**
 * Process Vue expressions {{ }} in text and replace with actual values
 */
const processTextExpressions = (text, context) => {
  const expressionRegex = /\{\{\s*([^}]+)\s*\}\}/g;
  
  return text.replace(expressionRegex, (match, expression) => {
    try {
      const result = evaluateExpression(expression.trim(), context);
      return result !== undefined ? String(result) : match;
    } catch (error) {
      console.warn('Error evaluating expression:', expression, error);
      return match; // Return original if evaluation fails
    }
  });
};

/**
 * Simple and safe expression evaluator
 */
const evaluateExpression = (expression, context) => {
  // Handle ternary operator (condition ? value1 : value2)
  if (expression.includes('?') && expression.includes(':')) {
    const ternaryRegex = /^(.+?)\s*\?\s*(.+?)\s*:\s*(.+)$/;
    const ternaryMatch = expression.match(ternaryRegex);
    if (ternaryMatch) {
      const [, condition, trueValue, falseValue] = ternaryMatch;
      const conditionResult = evaluateExpression(condition.trim(), context);
      return conditionResult ? 
        evaluateExpression(trueValue.trim(), context) : 
        evaluateExpression(falseValue.trim(), context);
    }
  }
  
  // Handle simple property access like "textfield", "textfield || 'default'"
  if (expression.includes('||')) {
    const [left, right] = expression.split('||').map(s => s.trim());
    const leftValue = getValueFromContext(left, context);
    if (leftValue) return leftValue;
    return evaluateExpression(right, context);
  }
  
  // Handle && expressions
  if (expression.includes('&&')) {
    const [left, right] = expression.split('&&').map(s => s.trim());
    const leftValue = getValueFromContext(left, context);
    if (!leftValue) return false;
    return evaluateExpression(right, context);
  }
  
  // Handle simple comparisons
  if (expression.includes('===')) {
    const [left, right] = expression.split('===').map(s => s.trim());
    const leftValue = getValueFromContext(left, context);
    const rightValue = evaluateExpression(right, context);
    return leftValue === rightValue;
  }
  
  if (expression.includes('==')) {
    const [left, right] = expression.split('==').map(s => s.trim());
    const leftValue = getValueFromContext(left, context);
    const rightValue = evaluateExpression(right, context);
    return leftValue == rightValue;
  }
  
  if (expression.includes('!==')) {
    const [left, right] = expression.split('!==').map(s => s.trim());
    const leftValue = getValueFromContext(left, context);
    const rightValue = evaluateExpression(right, context);
    return leftValue !== rightValue;
  }
  
  if (expression.includes('!=')) {
    const [left, right] = expression.split('!=').map(s => s.trim());
    const leftValue = getValueFromContext(left, context);
    const rightValue = evaluateExpression(right, context);
    return leftValue != rightValue;
  }
  
  // Handle negation
  if (expression.startsWith('!')) {
    const innerExpression = expression.slice(1).trim();
    const value = evaluateExpression(innerExpression, context);
    return !value;
  }
  
  // Handle method calls like "getCustomVariable('key', 'default')" or "nl2br(textfield)"
  const methodCallRegex = /(\w+)\s*\(\s*([^)]*)\s*\)/;
  const methodMatch = expression.match(methodCallRegex);
  if (methodMatch) {
    const [, methodName, argsString] = methodMatch;
    
    console.log('🔍 Method call detected:', methodName, 'with raw args:', argsString);
    console.log('🔍 Available methods in context:', Object.keys(context).filter(k => typeof context[k] === 'function'));
    
    if (context[methodName] && typeof context[methodName] === 'function') {
      const args = parseComplexArguments(argsString);
      
      // Handle cases where arguments are variable names that need to be resolved
      const resolvedArgs = args.map(arg => {
        // If the argument is a simple identifier (not a string literal), try to get its value from context
        if (typeof arg === 'string' && !/^['"]/.test(arg) && context.hasOwnProperty(arg)) {
          console.log('🔍 Resolving argument:', arg, '→', context[arg]);
          return context[arg];
        }
        return arg;
      });
      
      console.log('🔍 Calling method:', methodName, 'with resolved args:', resolvedArgs);
      const result = context[methodName](...resolvedArgs);
      console.log('🔍 Method result:', result);
      return result;
    } else {
      console.warn('🔍 Method not found in context:', methodName);
    }
  }
  
  // Handle method calls like "new Date().toLocaleTimeString()"
  if (expression.includes('new Date()')) {
    if (expression.includes('.toLocaleTimeString()')) {
      return new Date().toLocaleTimeString();
    }
    if (expression.includes('.toLocaleDateString()')) {
      return new Date().toLocaleDateString();
    }
    return new Date().toString();
  }
  
  // Handle string concatenation with + operator
  if (expression.includes('+') && !expression.includes('++') && !expression.includes('+=')) {
    const parts = splitExpression(expression, '+');
    if (parts.length > 1) {
      let result = '';
      for (const part of parts) {
        const value = evaluateExpression(part.trim(), context);
        result += String(value || '');
      }
      return result;
    }
  }
  
  // Handle simple property access
  return getValueFromContext(expression, context);
};

/**
 * Parse complex arguments for method calls
 */
const parseComplexArguments = (argsString) => {
  if (!argsString.trim()) return [];
  
  const shouldLog = argsString.length > 50 || argsString.includes('\\u');
  if (shouldLog) {
    console.log('Parsing complex arguments:', argsString.substring(0, 100) + (argsString.length > 100 ? '...' : ''));
  }
  
  const args = [];
  let current = '';
  let inQuotes = false;
  let quoteChar = '';
  let depth = 0;
  let i = 0;
  
  while (i < argsString.length) {
    const char = argsString[i];
    
    if (!inQuotes && (char === '"' || char === "'")) {
      inQuotes = true;
      quoteChar = char;
      current += char;
    } else if (inQuotes && char === quoteChar) {
      let escapeCount = 0;
      let j = i - 1;
      while (j >= 0 && argsString[j] === '\\') {
        escapeCount++;
        j--;
      }
      
      if (escapeCount % 2 === 0) {
        inQuotes = false;
        current += char;
      } else {
        current += char;
      }
    } else if (!inQuotes && char === '(') {
      depth++;
      current += char;
    } else if (!inQuotes && char === ')') {
      depth--;
      current += char;
    } else if (!inQuotes && char === ',' && depth === 0) {
      if (current.trim()) {
        args.push(parseComplexArgument(current.trim()));
      }
      current = '';
    } else {
      current += char;
    }
    
    i++;
  }
  
  if (current.trim()) {
    args.push(parseComplexArgument(current.trim()));
  }
  
  if (shouldLog) {
    console.log('Parsed arguments:', args);
  }
  return args;
};

/**
 * Parse a single complex argument with smart processing
 */
const parseComplexArgument = (arg) => {
  console.log('🔧 Parsing argument:', arg);
  
  // Handle quoted strings
  if ((arg.startsWith('"') && arg.endsWith('"')) || 
      (arg.startsWith("'") && arg.endsWith("'"))) {
    let unquoted = arg.slice(1, -1);
    
    // Only apply transformations if the string actually contains escape sequences or entities
    let processed = unquoted;
    
    // Check if we need HTML entity decoding (contains & or Unicode escapes)
    if (processed.includes('&') || processed.includes('\\u')) {
      processed = decodeHtmlEntities(processed);
    }
    
    // Check if we need escape sequence processing (contains backslashes)
    if (processed.includes('\\')) {
      processed = unescapeString(processed);
    }
    
    console.log('🔧 Argument processed:', unquoted, '→', processed);
    return processed;
  }
  
  // Handle numbers
  if (!isNaN(arg) && arg !== '') {
    return Number(arg);
  }
  
  // Handle booleans and special values
  if (arg === 'true') return true;
  if (arg === 'false') return false;
  if (arg === 'null') return null;
  if (arg === 'undefined') return undefined;
  
  // Return as-is for variable names and other identifiers
  return arg;
};

/**
 * Decode HTML entities
 */
const decodeHtmlEntities = (str) => {
  if (typeof str !== 'string') return str;
  
  // Log for debugging HTML entity issues
  const hasEntities = str.includes('&') || str.includes('\\u');
  if (hasEntities) {
    console.log('🔧 Decoding HTML entities:', str.substring(0, 100) + (str.length > 100 ? '...' : ''));
  }
  
  try {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = str;
    const result = textarea.value;
    
    if (hasEntities) {
      console.log('🔧 Decoded result:', result.substring(0, 100) + (result.length > 100 ? '...' : ''));
    }
    
    return result;
  } catch (error) {
    console.warn('Error decoding HTML entities:', error);
    return str; // Return original string if decoding fails
  }
};

/**
 * Unescape string escape sequences
 */
const unescapeString = (str) => {
  if (typeof str !== 'string') return str;
  
  // Log for debugging escape sequence issues
  const hasEscapes = str.includes('\\');
  if (hasEscapes) {
    console.log('🔧 Unescaping string:', str.substring(0, 100) + (str.length > 100 ? '...' : ''));
  }
  
  let result = str;
  
  // Handle Unicode escape sequences first (before other backslash replacements)
  result = result.replace(/\\u([0-9a-fA-F]{4})/g, (match, hex) => {
    return String.fromCharCode(parseInt(hex, 16));
  });
  
  // Handle hex escape sequences
  result = result.replace(/\\x([0-9a-fA-F]{2})/g, (match, hex) => {
    return String.fromCharCode(parseInt(hex, 16));
  });
  
  // Handle common escape sequences
  result = result
    .replace(/\\n/g, '\n')      // newline
    .replace(/\\r/g, '\r')      // carriage return
    .replace(/\\t/g, '\t')      // tab
    .replace(/\\b/g, '\b')      // backspace
    .replace(/\\f/g, '\f')      // form feed
    .replace(/\\v/g, '\v')      // vertical tab
    .replace(/\\0/g, '\0')      // null character
    .replace(/\\'/g, "'")       // single quote
    .replace(/\\"/g, '"')       // double quote
    .replace(/\\\//g, '/')      // forward slash (JSON escape)
    .replace(/\\\\/g, '\\');    // backslash (must be last)
  
  if (hasEscapes) {
    console.log('🔧 Unescaped result:', result.substring(0, 100) + (result.length > 100 ? '...' : ''));
  }
  
  return result;
};

/**
 * Get value from context
 */
const getValueFromContext = (key, context) => {
  const cleanKey = key.replace(/['"]/g, '');
  
  if (key !== cleanKey) {
    return cleanKey;
  }
  
  const methodCallRegex = /(\w+)\s*\(\s*([^)]*)\s*\)/;
  const methodMatch = cleanKey.match(methodCallRegex);
  if (methodMatch) {
    const [, methodName, argsString] = methodMatch;
    
    if (context[methodName] && typeof context[methodName] === 'function') {
      const args = parseComplexArguments(argsString);
      console.log('Calling method:', methodName, 'with args:', args);
      return context[methodName](...args);
    }
  }
  
  if (context.hasOwnProperty(cleanKey)) {
    return context[cleanKey];
  }
  
  if (cleanKey.includes('.')) {
    const parts = cleanKey.split('.');
    let value = context;
    for (const part of parts) {
      if (value && value.hasOwnProperty(part)) {
        value = value[part];
      } else {
        return undefined;
      }
    }
    return value;
  }
  
  return undefined;
};

/**
 * Split expression by operator while respecting parentheses and quotes
 */
const splitExpression = (expression, operator) => {
  const parts = [];
  let current = '';
  let depth = 0;
  let inQuotes = false;
  let quoteChar = '';
  
  for (let i = 0; i < expression.length; i++) {
    const char = expression[i];
    
    if (!inQuotes && (char === '"' || char === "'")) {
      inQuotes = true;
      quoteChar = char;
      current += char;
    } else if (inQuotes && char === quoteChar) {
      inQuotes = false;
      current += char;
    } else if (!inQuotes && char === '(') {
      depth++;
      current += char;
    } else if (!inQuotes && char === ')') {
      depth--;
      current += char;
    } else if (!inQuotes && char === operator && depth === 0) {
      parts.push(current);
      current = '';
    } else {
      current += char;
    }
  }
  
  if (current) {
    parts.push(current);
  }
  
  return parts.length > 1 ? parts : [expression];
};

/**
 * Create a render function that can handle both Vue compile and manual processing
 */
export const createTemplateRenderer = (componentInstance) => {
  return (html, context = {}) => {
    const compileFunction = getCompileFunction();
    
    // Environment detection
    const isProduction = (typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'production') || 
                         (typeof window !== 'undefined' && !window.location.hostname.includes('localhost') && !window.location.hostname.includes('127.0.0.1'));
    
    console.log('Template renderer environment:', {
      isProduction,
      hasCompileFunction: !!compileFunction,
      hostname: typeof window !== 'undefined' ? window.location.hostname : 'unknown'
    });

    // Try Vue compile first in development
    if (!isProduction && compileFunction && typeof compileFunction === 'function') {
      try {
        console.log('Attempting to compile template with Vue compile (development):', html);
        
        const compiledRender = compileFunction(html);

        // Create a temporary component with the compiled template
        const tempComponent = {
          render: compiledRender,
          data: () => context,
          methods: componentInstance.$options?.methods || {},
          computed: componentInstance.$options?.computed || {},
          props: componentInstance.$props || {}
        };

        console.log('✅ Vue compile successful');
        return h(tempComponent);
      } catch (error) {
        console.warn('Vue compile failed in development, falling back to stable section render:', error.message);
      }
    }

    // Stable section render approach - handles mixed stable/dynamic content
    console.log('Using stable section render approach');
    try {
      // Automatically mark sections with images as stable
      const markedHtml = autoMarkStableSections(html);
      
      let vnode;
      if (markedHtml !== html) {
        console.log('🏷️ Auto-marked stable sections in template');
        vnode = renderTemplateWithStableSections(markedHtml, context, componentInstance);
      } else {
        // No stable sections found, use normal rendering
        vnode = createVNodeFromHtml(html, context, componentInstance);
      }
      
      if (!vnode) {
        return h('div', 'No content to render');
      }
      
      // If the root element is a single container div from parsing, unwrap its children
      if (vnode.type === 'div' && vnode.children && vnode.children.length > 0) {
        // Return the children directly wrapped in a fragment for multiple elements
        if (vnode.children.length === 1) {
          return vnode.children[0];
        } else {
          return h(Fragment, {}, vnode.children);
        }
      }
      
      console.log('✅ Stable section render successful');
      return vnode;
    } catch (error) {
      console.error('Error with stable section render, falling back to innerHTML approach:', error);
      
      // Fallback to the original innerHTML approach if VNode creation fails
      try {
        const processedHtml = processHtmlForReactivity(html, context);
        
        console.log('✅ Fallback innerHTML render successful');
        return h('div', {
          innerHTML: processedHtml,
          // Add event handling for dynamic content
          onVnodeMounted: (vnode) => {
            // Set up event listeners after the content is mounted
            if (componentInstance && componentInstance.setupDynamicContent) {
              componentInstance.$nextTick(() => {
                componentInstance.setupDynamicContent();
              });
            }
          },
          onVnodeUpdated: (vnode) => {
            // Re-setup event listeners after updates
            if (componentInstance && componentInstance.setupDynamicContent) {
              componentInstance.$nextTick(() => {
                componentInstance.setupDynamicContent();
              });
            }
          }
        });
      } catch (fallbackError) {
        console.error('Error with fallback render:', fallbackError);
        return h('div', {
          style: {
            padding: '20px',
            borderRadius: '8px',
            background: 'linear-gradient(135deg, #ff6b6b, #ffa726)',
            color: 'white',
            textAlign: 'center',
            fontWeight: 'bold'
          }
        }, `Template rendering error: ${error.message}`);
      }
    }
  };
}; 